<template>
  <form class="flex flex-col gap-6 md:flex-row" @submit.prevent="onSubmit">
    <SFValidatedInputGroup
      v-slot="{ isValid }"
      :errors="v.email?.$errors"
      class="flex-1 md:max-w-[560px]"
    >
      <SFTextInput
        id="newsletter-email"
        v-model="userPayload.email"
        :has-errors="!isValid"
        :placeholder="$t('newsletter.email_placeholder')"
        :readonly="isSubmitting"
        autocomplete="email"
        class="w-full text-base focus:ring-0"
        name="newsletter-email"
        no-label
        required
        type="email"
        @change="v.email.$touch()"
      />
      <span class="absolute left-2 top-3 text-red-500">*</span>
    </SFValidatedInputGroup>
    <div class="min-w-64">
      <SFButton
        :disabled="isSubmitting || v.$errors.length"
        class="flex justify-center py-4 !text-xl !font-bold disabled:cursor-not-allowed disabled:!border-anthracite-300 disabled:!bg-anthracite-450 disabled:!text-anthracite-100"
        f
        is-full-width
        size="xl"
        type="submit"
        variant="primary"
      >
        {{ $t('newsletter.subscribe') }}
      </SFButton>
    </div>
  </form>
</template>
<script lang="ts" setup>
import useVuelidate from '@vuelidate/core'
import { reactive, watch } from 'vue'
import type { AxiosError } from 'axios'
import {
  SFButton,
  SFTextInput,
  SFValidatedInputGroup,
} from '#storefront-ui/components'
import { useSnipesApi, useValidationRules } from '~/composables'
import { onBeforeRouteUpdate, useRoute } from '#app/composables/router'

const emit = defineEmits<{
  onResponse: [status: string, data?: { email: string; referenceKey: string }]
}>()

const validationRules = useValidationRules()
const route = useRoute()
const { newsletterSubscribe, isLoading: isSubmitting } = useSnipesApi()
const rules = {
  email: {
    required: validationRules.required,
    email: validationRules.email,
  },
}

const userPayload = reactive<Record<'email', string>>({
  email: '',
})

const v = useVuelidate(rules, userPayload, { $autoDirty: true })

const onSubmit = async () => {
  const isValid = await v.value.$validate()

  if (!isValid) {
    return
  }

  try {
    const { status, data } = await newsletterSubscribe(userPayload)
    emit('onResponse', status, data)
    userPayload.email = ''
    v.value.$reset()
  } catch (err) {
    const error = err as AxiosError
    const status = error?.response?.data?.status || 'declined'
    const code = error?.response?.data?.code || 404
    emit('onResponse', status, code)
  }
}

// Reset form and clear validation errors whenever the route changes
watch(
  () => route.fullPath,
  () => {
    userPayload.email = ''
    v.value.$reset()
  },
)

// Clear just before navigating away (covers in-component navigation updates)
onBeforeRouteUpdate(() => {
  userPayload.email = ''
  v.value.$reset()
})
</script>
